import React, { useState, useEffect } from 'react'
import H from '../data'
import HI from '../components/Icons'
import { Av, Stars, StatusBadge, Delta, Kpi, Banner, Empty, SkeletonRows, BarChart, Sparkline, CardHead, money } from '../components/SharedComponents'

/* ============ CoCafe Host -- Revenue & Analytics ============ */
function HeatGrid() {
  const color = (v) => {
    // green scale by intensity
    const a = Math.max(0.06, v / 100);
    return `color-mix(in srgb, var(--green) ${Math.round(a * 100)}%, var(--surface))`;
  };
  return (
    <div style={{ overflowX: "auto" }}>
      <div style={{ display: "grid", gridTemplateColumns: "60px repeat(7, 48px)", gap: 6, minWidth: 500 }}>
        <div />
        {H.peakDays.map((d) => <div key={d} className="t-xs" style={{ textAlign: "center", fontWeight: 700, color: "var(--ink-2)", whiteSpace: "nowrap" }}>{d}</div>)}
        {H.peakHours.map((row) => (
          <React.Fragment key={row.label}>
            <div className="t-xs muted mono" style={{ alignSelf: "center", textAlign: "right", paddingRight: 6 }}>{row.label}</div>
            {row.v.map((v, i) => (
              <div key={i} className="heat" style={{ background: color(v), color: v > 60 ? "#fff" : "var(--ink-2)" }} title={H.peakDays[i] + " " + row.label + " . " + v + "%"}>
                {v >= 88 ? v : ""}
              </div>
            ))}
          </React.Fragment>
        ))}
      </div>
      <div className="row g8" style={{ marginTop: 14, justifyContent: "flex-end" }}>
        <span className="t-xs muted">Low</span>
        {[15, 35, 55, 75, 95].map((v) => <span key={v} style={{ width: 18, height: 12, borderRadius: 3, background: `color-mix(in srgb, var(--green) ${v}%, var(--surface))` }} />)}
        <span className="t-xs muted">High</span>
      </div>
    </div>
  );
}

function Analytics({ toast }) {
  const [range, setRange] = useState("weekly");
  const [loading, setLoading] = useState(true);
  const totalThisMonth = 41200;
  const data = range === "weekly" ? H.week : H.monthly;

  useEffect(() => {
    const t = setTimeout(() => setLoading(false), 650);
    return () => clearTimeout(t);
  }, []);

  if (loading) {
    return (
      <div className="page fade-in">
        <div className="metrics">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="card card-pad" style={{ height: 110 }}>
              <div style={{ height: 12, background: 'var(--border)', borderRadius: 4, marginBottom: 10 }} />
              <div style={{ height: 24, background: 'var(--border)', borderRadius: 4, marginBottom: 10 }} />
              <div style={{ height: 12, background: 'var(--border)', borderRadius: 4 }} />
            </div>
          ))}
        </div>
        <div className="card" style={{ marginBottom: 20, height: 280 }}>
          <div style={{ padding: 16 }}>
            <SkeletonRows rows={3} cols={1} />
          </div>
        </div>
        <div className="chart-container">
          <div className="card" style={{ height: 280 }}>
            <div style={{ padding: 16 }}>
              <SkeletonRows rows={4} cols={1} />
            </div>
          </div>
          <div className="card" style={{ height: 280 }}>
            <div style={{ padding: 16 }}>
              <SkeletonRows rows={4} cols={1} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page fade-in">

      {/* KPI strip */}
      <div className="metrics">
        <Kpi label="Revenue . Jun (MTD)" icon={<HI.wallet s={15} />} value={money(totalThisMonth)} footer={<><Delta value={12} /><span className="muted">vs May</span></>} />
        <Kpi label="Avg. occupancy" icon={<HI.seat s={15} />} value="76%" footer={<><Delta value={4} /><span className="muted">desk-hours</span></>} />
        <Kpi label="Avg. booking value" icon={<HI.tag s={15} />} value={money(412)} footer={<><Delta value={-3} /><span className="muted">shorter sessions</span></>} />
        <Kpi label="Repeat guests" icon={<HI.refresh s={15} />} value="41%" footer={<><Delta value={7} /><span className="muted">of bookings</span></>} />
      </div>

      {/* revenue chart */}
      <div className="card" style={{ marginBottom: 20 }}>
        <CardHead title="Revenue" sub={range === "weekly" ? "This week, by day" : "Last 6 months"}
          right={<div className="seg">
            {["daily", "weekly", "monthly"].map((r) => <button key={r} className={range === r ? "on" : ""} onClick={() => setRange(r)}>{r[0].toUpperCase() + r.slice(1)}</button>)}
          </div>} />
        <div className="card-pad">
          {range === "daily" ? (
            <div>
              <div className="row between" style={{ marginBottom: 10 }}>
                <div><div className="mono" style={{ fontSize: 24, fontWeight: 800 }}>{money(3610)}</div><div className="t-xs muted">Today . running total</div></div>
                <span className="badge green"><HI.trend s={12} />+18% vs yesterday</span>
              </div>
              <Sparkline data={H.rev30} w={640} h={120} />
              <div className="row between t-xs muted" style={{ marginTop: 6 }}><span>30 days ago</span><span>Today</span></div>
            </div>
          ) : (
            <BarChart data={data} valueKey="rev" height={200} format={(v) => "₹" + (v / 1000).toFixed(0) + "k"} />
          )}
        </div>
        {range === "monthly" && <div className="card-foot t-sm muted">June is partial (10 days in). On current pace you are tracking ~{money(123000)} for the month - your best yet.</div>}
      </div>

      {/* peak hours + session mix */}
      <div className="chart-container">
        <div className="card">
          <CardHead title="Peak booking hours" sub="When your desks are busiest" />
          <div className="card-pad"><HeatGrid /></div>
          <div className="card-foot"><Banner tone="green" icon={<HI.trend s={16} />}>Your hottest window is <strong>Fri & Sat, 4-6pm</strong> (90-94%). You could raise rates here or add evening capacity.</Banner></div>
        </div>

        <div className="card">
          <CardHead title="Popular session lengths" sub="Share of bookings" />
          <div className="card-pad col g14">
            {H.sessionMix.map((s) => (
              <div key={s.label}>
                <div className="row between t-sm" style={{ marginBottom: 5 }}>
                  <span style={{ fontWeight: 600 }}>{s.label}</span>
                  <span className="muted">{s.share}% . {s.n}</span>
                </div>
                <div className="meter"><span style={{ width: s.share + "%" }} /></div>
              </div>
            ))}
          </div>
          <div className="card-foot t-sm muted">3-hour sessions are your bread and butter. Bundling a 3h + coffee deal could lift average value.</div>
        </div>
      </div>

      {/* occupancy trend + payout */}
      <div className="chart-container">
        <div className="card">
          <CardHead title="Occupancy trend" sub="Desk-hours filled, this week" />
          <div className="card-pad"><BarChart data={H.week} valueKey="occ" height={150} format={(v) => v + "%"} /></div>
        </div>

        <div className="card">
          <CardHead title="Next payout" />
          <div className="card-pad">
            <div className="mono" style={{ fontSize: "clamp(24px, 6vw, 30px)", fontWeight: 800, letterSpacing: "-.02em" }}>{money(H.host.payoutAmount)}</div>
            <div className="t-sm muted" style={{ marginTop: 4 }}>Arrives <strong className="ink2">{H.host.payoutNext}</strong> . {H.host.payoutMethod}</div>
            <hr className="hr" style={{ margin: "16px 0" }} />
            <div className="col g9">
              <div className="row between t-sm"><span className="muted">Gross bookings (this cycle)</span><span className="mono">{money(19390)}</span></div>
              <div className="row between t-sm"><span className="muted">CoCafe service fee (5%)</span><span className="mono muted">-{money(970)}</span></div>
              <div className="row between"><span style={{ fontWeight: 700 }}>Net payout</span><span className="mono" style={{ fontWeight: 700 }}>{money(H.host.payoutAmount)}</span></div>
            </div>
            <button className="btn btn-ghost btn-block" style={{ marginTop: 16 }} onClick={() => toast("Opening payout history")}>Payout history <HI.arrowR s={14} /></button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Analytics;
